import { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import SimpleSplash from './components/SimpleSplash';
import { SubscriptionGate } from './components/SubscriptionGate';
import { AppSignInCard } from './components/AppSignInCard';
import { WebAccessLinkOffer } from './components/WebAccessLinkOffer';

// Pages
import { Home } from "./pages/Home";
import { History } from "./pages/History";
import { HistoricalView } from "./pages/HistoricalView";
import { Export } from "./pages/Export";
import { Settings } from "./pages/Settings";
import { Help } from "./pages/Help";
import { Passcode } from "./pages/Passcode";
import { AIResponse } from "./pages/AIResponse";
import { PaypalReturn } from "./pages/PaypalReturn";
import { PaypalCancel } from "./pages/PaypalCancel";

// Components
import { AppLayout } from "./components/AppLayout";
import { ThemeProvider } from "./components/ThemeProvider";

// Utils
import {
  hasCompletedOnboarding,
  getSecurityType,
  SECURITY_TYPE,
  getColorTheme,
  getTextScale,
  applyTextScale,
  getSettings,
  getEntries,
  getAiTokenUsageDay,
  saveSettings,
  mergeCloudEntriesIntoLocal,
  resetLocalAccountCacheOnUserChange,
  saveColorTheme,
  saveCustomThemePalette,
  saveTextScale,
  saveVoicePrefs,
  setHomeChatState,
  setHistoryWeeklySummaryCache,
  getHomeChatState,
  pickPreferredHomeChatState,
  getHasSeenWebAccessOffer,
  setHasSeenWebAccessOffer,
  getWebAccessLinkOfferNeverAsk,
  setWebAccessLinkOfferNeverAsk,
  formatDate,
} from "./utils/storage";
import { scheduleNotification, initializeNotifications } from "./utils/notifications";
import { generateHistorySummary, getHistorySummaryFirestoreKey } from './utils/aiCompanion';
import { isBiometricsAvailable } from './utils/biometricAuth';
import { applyThemeIcon } from './utils/themeIcon';
import {
  signInWithGoogle,
  signInWithApple,
  signInWithEmailPassword,
  subscribeToAuth,
  consumeWebAuthRedirectResult,
  getFirebaseConfigDiagnostics,
  getLinkedProviderIdsLive,
  setLastKnownAuthUid,
  APPLE_WEB_SIGNIN_PENDING_KEY,
  GOOGLE_WEB_SIGNIN_PENDING_KEY,
} from './utils/firebase';
import { getCloudJournalEntries, getCloudUserPreferences } from './utils/cloudApi';
import { requiresNativeSubscriptionGate, checkActiveProSubscription } from './utils/nativeSubscriptions';
import {
  AI_LIMITS_ENFORCED,
  ensureAiTrialStartedForUid,
  getAiUsageTierForUid,
  getDisplayDailyTokenCapForUid,
  setCachedNativeSubActiveForUid,
} from './utils/aiTokenEntitlements';
import { runFreemiumOpenSubscriptionNudge } from './utils/aiSubscriptionNudges';
import {
  refreshUserFirestoreEntitlement,
  startUserFirestoreEntitlementListener,
  stopUserFirestoreEntitlementListener,
} from './utils/userFirestoreEntitlement';
import { canOfferAppleSignIn } from './utils/appleAuthAvailability';
import { shouldShowAppleSignInUi, shouldOfferLinkAppleNudge } from './utils/authPolicy';
import { deriveSignInFirstName } from './utils/onboardingCoach';

import "./App.css";

const TERMS_ACCEPTED_STORAGE_KEY = 'rbmProTermsAccepted';
const DEMO_MODE = String(process.env.REACT_APP_DEMO_MODE || '').trim().toLowerCase() === 'true';

function App() {
  const toastTopOffset = 'calc(env(safe-area-inset-top, 0px) + 92px)';
  const toastLeftOffset = 'calc(env(safe-area-inset-left, 0px) + 12px)';
  const toastMaxWidth = 'min(520px, calc(100vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px) - 32px))';

  const [showSplash, setShowSplash] = useState(true);
  const [isOnboarded, setIsOnboarded] = useState(hasCompletedOnboarding());
  const [isLocked, setIsLocked] = useState(false);
  const [checkComplete, setCheckComplete] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [accountSyncChecked, setAccountSyncChecked] = useState(false);
  const needsNativeIap = requiresNativeSubscriptionGate();
  const [nativeSubChecked, setNativeSubChecked] = useState(!needsNativeIap);
  const [nativeSubActive, setNativeSubActive] = useState(!needsNativeIap);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [authError, setAuthError] = useState('');
  const [demoEmail, setDemoEmail] = useState('');
  const [demoPassword, setDemoPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(() => {
    try {
      return localStorage.getItem(TERMS_ACCEPTED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const resumeCloudPullTimerRef = useRef(null);
  const summaryWarmupTimerRef = useRef(null);
  const summaryWarmupInFlightRef = useRef(false);
  const summaryWarmupLastKeyRef = useRef('');
  const lastAuthUserRef = useRef(null);
  const authListenerHydratedRef = useRef(false);
  const [webAccessOfferOpen, setWebAccessOfferOpen] = useState(false);
  const [webAccessMissingProvider, setWebAccessMissingProvider] = useState(null);
  const firebaseDiagnostics = getFirebaseConfigDiagnostics();
  const canShowAppleAuth = shouldShowAppleSignInUi();
  const todayYmd = formatDate(new Date());
  const usageToday = getAiTokenUsageDay();
  const aiDailyCap = currentUser?.uid ? getDisplayDailyTokenCapForUid(currentUser.uid) : 0;
  const aiTier = currentUser?.uid ? getAiUsageTierForUid(currentUser.uid) : 'freemium';
  const isAiHardBlocked =
    AI_LIMITS_ENFORCED
    && Number.isFinite(aiDailyCap)
    && aiDailyCap > 0
    && usageToday.date === todayYmd
    && usageToday.used >= aiDailyCap;
  const shouldShowSubscriptionPaywall =
    Boolean(currentUser)
    && accountSyncChecked
    && needsNativeIap
    && nativeSubChecked
    && !nativeSubActive
    && AI_LIMITS_ENFORCED
    && isAiHardBlocked;
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
  }, []);

  const handleAcceptedTermsChange = useCallback((e) => {
    const checked = Boolean(e.target.checked);
    setAcceptedTerms(checked);
    setAuthError('');
    try {
      localStorage.setItem(TERMS_ACCEPTED_STORAGE_KEY, checked ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  }, []);

  /** Light pull when returning to the app (another device may have updated chat / journal). */
  const scheduleResumeCloudPull = useCallback(() => {
    if (!currentUser?.uid || !accountSyncChecked) return;
    if (resumeCloudPullTimerRef.current) window.clearTimeout(resumeCloudPullTimerRef.current);
    resumeCloudPullTimerRef.current = window.setTimeout(async () => {
      resumeCloudPullTimerRef.current = null;
      try {
        const cloudPrefs = await getCloudUserPreferences().catch(() => ({}));
        if (cloudPrefs?.homeChatState && typeof cloudPrefs.homeChatState === 'object') {
          setHomeChatState(pickPreferredHomeChatState(cloudPrefs.homeChatState, getHomeChatState()));
        }
        const entries = await getCloudJournalEntries(100).catch(() => []);
        const merged = mergeCloudEntriesIntoLocal(entries || []);
        if (merged > 0) window.dispatchEvent(new Event('focus'));
      } catch (e) {
        console.warn('Resume cloud pull skipped:', e?.message || e);
      }
    }, 700);
  }, [currentUser?.uid, accountSyncChecked]);

  const scheduleHistorySummaryWarmup = useCallback((delayMs = 1200) => {
    if (!currentUser?.uid || !accountSyncChecked || !checkComplete || !isOnboarded) return;
    if (summaryWarmupTimerRef.current) window.clearTimeout(summaryWarmupTimerRef.current);
    summaryWarmupTimerRef.current = window.setTimeout(async () => {
      summaryWarmupTimerRef.current = null;
      if (summaryWarmupInFlightRef.current) return;
      const all = getEntries() || {};
      const entriesArray = Object.entries(all)
        .map(([date, entry]) => ({ date, entry }))
        .filter((row) => /^\d{4}-\d{2}-\d{2}$/.test(String(row.date || '')))
        .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
      if (!entriesArray.length) return;

      const uniqueDaysCount = new Set(entriesArray.map((e) => e.date)).size;
      const now = new Date();
      const today = formatDate(now);
      const d = new Date(now);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 4 - (d.getDay() || 7));
      const yearStart = new Date(d.getFullYear(), 0, 1);
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      const weekKey = `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
      const warmupKey = `${today}:${weekKey}:${entriesArray[0]?.date || ''}:${entriesArray.length}`;
      if (summaryWarmupLastKeyRef.current === warmupKey) return;

      summaryWarmupInFlightRef.current = true;
      try {
        const weeklyRows = entriesArray.slice(0, 7);
        const weekly = await generateHistorySummary('weekly', weeklyRows, {});
        if (weekly?.success && weekly.response) {
          setHistoryWeeklySummaryCache(today, weekly.response, {
            range: 'weekly',
            weekKey,
            rangeKey: getHistorySummaryFirestoreKey('weekly', weeklyRows),
          });
        }
        if (uniqueDaysCount >= 14) {
          const monthlyRows = entriesArray.slice(0, 30);
          const monthly = await generateHistorySummary('monthly', monthlyRows, {});
          if (monthly?.success && monthly.response) {
            setHistoryWeeklySummaryCache(today, monthly.response, {
              range: 'monthly',
              weekKey,
              rangeKey: getHistorySummaryFirestoreKey('monthly', monthlyRows),
            });
          }
        }
        if (uniqueDaysCount >= 60) {
          const yearlyRows = entriesArray.slice(0, 365);
          const yearly = await generateHistorySummary('yearly', yearlyRows, {});
          if (yearly?.success && yearly.response) {
            setHistoryWeeklySummaryCache(today, yearly.response, {
              range: 'yearly',
              weekKey,
              rangeKey: getHistorySummaryFirestoreKey('yearly', yearlyRows),
            });
          }
        }
        summaryWarmupLastKeyRef.current = warmupKey;
      } catch (e) {
        console.warn('History summary warmup skipped:', e?.message || e);
      } finally {
        summaryWarmupInFlightRef.current = false;
      }
    }, Math.max(250, Number(delayMs) || 0));
  }, [currentUser?.uid, accountSyncChecked, checkComplete, isOnboarded]);

  useEffect(() => {
    const cap = globalThis?.Capacitor;
    const pluginHeaders = Array.isArray(cap?.PluginHeaders) ? cap.PluginHeaders : [];
    const pluginNames = pluginHeaders
      .map((item) => item?.name)
      .filter((name) => typeof name === 'string');
    const hasFirebaseHeader = pluginNames.includes('FirebaseAuthentication');
    const isAvailable = typeof cap?.isPluginAvailable === 'function'
      ? cap.isPluginAvailable('FirebaseAuthentication')
      : false;

    console.log('🔌 Capacitor plugin diagnostics:', {
      platform: typeof cap?.getPlatform === 'function' ? cap.getPlatform() : 'unknown',
      isNativePlatform: typeof cap?.isNativePlatform === 'function' ? cap.isNativePlatform() : false,
      firebaseAuthenticationAvailable: isAvailable,
      hasFirebasePluginHeader: hasFirebaseHeader,
      pluginHeaderCount: pluginNames.length,
      pluginHeaders: pluginNames,
    });
  }, []);

  const formatSignInError = (error) => {
    if (!error) return 'Unknown error';
    if (error.message) return String(error.message);
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isSigningIn) return;
    if (!acceptedTerms) {
      setAuthError('Please accept the Privacy Policy and Terms of Service before signing in.');
      return;
    }
    setAuthError('');
    setIsSigningIn(true);
    console.log('Google sign-in button pressed');
    try {
      try {
        sessionStorage.setItem('rbm_post_sign_in_home', '1');
      } catch {
        /* ignore */
      }
      try {
        sessionStorage.setItem(GOOGLE_WEB_SIGNIN_PENDING_KEY, '1');
      } catch {
        /* ignore */
      }
      void signInWithGoogle()
        .then(() => {
          try {
            sessionStorage.setItem('rbm_post_sign_in_home', '1');
          } catch {
            /* ignore */
          }
        })
        .catch((error) => {
          try {
            sessionStorage.removeItem(GOOGLE_WEB_SIGNIN_PENDING_KEY);
          } catch {
            /* ignore */
          }
          const message = formatSignInError(error);
          console.error('Sign-in failed:', message);
          setAuthError(message);
          setIsSigningIn(false);
        });
      console.log('Sign-in flow started');
    } catch (error) {
      const message = formatSignInError(error);
      console.error('Sign-in failed:', message);
      setAuthError(message);
      setIsSigningIn(false);
    }
  };

  const handleAppleSignIn = async () => {
    if (isSigningIn) return;
    if (!acceptedTerms) {
      setAuthError('Please accept the Privacy Policy and Terms of Service before signing in.');
      return;
    }
    setAuthError('');
    setIsSigningIn(true);
    try {
      try {
        sessionStorage.setItem('rbm_post_sign_in_home', '1');
      } catch {
        /* ignore */
      }
      try {
        sessionStorage.setItem(APPLE_WEB_SIGNIN_PENDING_KEY, '1');
      } catch {
        /* ignore */
      }
      void signInWithApple()
        .then(() => {
          try {
            sessionStorage.setItem('rbm_post_sign_in_home', '1');
          } catch {
            /* ignore */
          }
        })
        .catch((error) => {
          try {
            sessionStorage.removeItem(APPLE_WEB_SIGNIN_PENDING_KEY);
          } catch {
            /* ignore */
          }
          const message = formatSignInError(error);
          console.error('Sign-in failed:', message);
          setAuthError(message);
          setIsSigningIn(false);
        });
    } catch (error) {
      try {
        sessionStorage.removeItem(APPLE_WEB_SIGNIN_PENDING_KEY);
      } catch {
        /* ignore */
      }
      const message = formatSignInError(error);
      console.error('Sign-in failed:', message);
      setAuthError(message);
      setIsSigningIn(false);
    }
  };

  const handleDemoSignIn = async () => {
    if (!DEMO_MODE || isSigningIn) return;
    if (!acceptedTerms) {
      setAuthError('Please accept the Privacy Policy and Terms of Service before signing in.');
      return;
    }
    setAuthError('');
    setIsSigningIn(true);
    try {
      const cred = await signInWithEmailPassword(demoEmail, demoPassword);
      const user = cred?.user || null;
      try {
        sessionStorage.setItem('rbm_post_sign_in_home', '1');
      } catch {
        /* ignore */
      }
      // Native iOS can delay auth observer hydration; bridge immediately so the UI can continue.
      if (user?.uid) {
        setLastKnownAuthUid(user.uid);
        setCurrentUser(user);
        setAuthReady(true);
        setAccountSyncChecked(false);
        void refreshUserFirestoreEntitlement(user.uid).finally(() => {
          startUserFirestoreEntitlementListener(user.uid);
        });
      }
      setIsSigningIn(false);
    } catch (error) {
      const message = formatSignInError(error);
      setAuthError(message);
      setIsSigningIn(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};
    unsubscribe = subscribeToAuth((user) => {
      if (!authListenerHydratedRef.current) {
        authListenerHydratedRef.current = true;
      }
      lastAuthUserRef.current = user;

      if (user?.uid) {
        setLastKnownAuthUid(user.uid);
        const switchedAccount = resetLocalAccountCacheOnUserChange(user.uid);
        if (switchedAccount) {
          setIsOnboarded(false);
        }
        void refreshUserFirestoreEntitlement(user.uid).finally(() => {
          startUserFirestoreEntitlementListener(user.uid);
        });
      } else {
        setLastKnownAuthUid('');
        stopUserFirestoreEntitlementListener();
      }
      // Always re-evaluate onboarding state on auth transitions so a deleted/recreated
      // account cannot inherit stale in-memory onboarding UI state.
      setIsOnboarded(hasCompletedOnboarding());
      setCurrentUser(user);
      setAuthReady(true);
      setAccountSyncChecked(!user);
      if (user) {
        setIsSigningIn(false);
        setAuthError('');
      }
    });
    void (async () => {
      try {
        await consumeWebAuthRedirectResult();
      } catch (e) {
        console.warn('Redirect auth finalize skipped:', e?.message || e);
      } finally {
        try {
          if (!cancelled) {
            if (sessionStorage.getItem(APPLE_WEB_SIGNIN_PENDING_KEY) === '1') {
              sessionStorage.removeItem(APPLE_WEB_SIGNIN_PENDING_KEY);
              setIsSigningIn(false);
            }
            if (sessionStorage.getItem(GOOGLE_WEB_SIGNIN_PENDING_KEY) === '1') {
              sessionStorage.removeItem(GOOGLE_WEB_SIGNIN_PENDING_KEY);
              setIsSigningIn(false);
            }
          }
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
      unsubscribe();
      setLastKnownAuthUid('');
      stopUserFirestoreEntitlementListener();
    };
  }, []);

  // First useEffect: Check onboarding and security
  useEffect(() => {
    console.log('🔒 App: Initializing...');
    
    const initialize = async () => {
      // Keep app icon aligned with saved theme even when app starts on the lock screen.
      void applyThemeIcon(getColorTheme());
      applyTextScale(getTextScale());

      const onboarded = hasCompletedOnboarding();
      setIsOnboarded(onboarded);
      
      // Check security type properly
      const securityType = getSecurityType();
      const hasSecurity = securityType === SECURITY_TYPE.PASSCODE || securityType === SECURITY_TYPE.BIOMETRIC;
      
      console.log('🔒 App: Security type:', securityType);
      console.log('🔒 App: Has security:', hasSecurity);
      
      if (onboarded && hasSecurity) {
        console.log('🔒 App: Security enabled, app locked');
        setIsLocked(true);
      } else {
        console.log('🔒 App: No security, app unlocked');
        setIsLocked(false);
      }
      
      setCheckComplete(true);

      // Setup notifications
      const setupNotifications = async () => {
        await initializeNotifications();
        const notifSettings = await import('./utils/storage').then(m => m.getNotificationSettings());
        if (notifSettings.enabled) {
          await scheduleNotification(notifSettings.time);
        }
      };
      setupNotifications();
    };
    
    initialize();
  }, []);

  // Second useEffect: Check biometrics availability
  useEffect(() => {
    const checkBiometrics = async () => {
      const available = await isBiometricsAvailable();
      console.log('📱 App: Biometrics available:', available);
    };
    checkBiometrics();
  }, []);

  // Third useEffect: Listen for app state changes
  useEffect(() => {
    const handleAppStateChange = ({ isActive }) => {
      console.log('🔒 App: App state changed, isActive:', isActive);
      if (!isActive && isOnboarded) {
        const securityType = getSecurityType();
        const hasSecurity = securityType === SECURITY_TYPE.PASSCODE || securityType === SECURITY_TYPE.BIOMETRIC;
        if (hasSecurity) {
          console.log('🔒 App: App went to background, locking');
          setIsLocked(true);
        }
      }
      if (isActive && needsNativeIap && currentUser?.uid) {
        void checkActiveProSubscription(currentUser.uid).then(setNativeSubActive);
      }
      if (isActive && currentUser?.uid) {
        scheduleResumeCloudPull();
        runFreemiumOpenSubscriptionNudge(currentUser.uid);
        scheduleHistorySummaryWarmup(1200);
        void refreshUserFirestoreEntitlement(currentUser.uid);
      }
    };

    CapacitorApp.addListener('appStateChange', handleAppStateChange);

    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, [isOnboarded, needsNativeIap, currentUser?.uid, scheduleResumeCloudPull, scheduleHistorySummaryWarmup]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      scheduleResumeCloudPull();
      scheduleHistorySummaryWarmup(900);
      if (currentUser?.uid) {
        runFreemiumOpenSubscriptionNudge(currentUser.uid);
        void refreshUserFirestoreEntitlement(currentUser.uid);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (resumeCloudPullTimerRef.current) {
        window.clearTimeout(resumeCloudPullTimerRef.current);
        resumeCloudPullTimerRef.current = null;
      }
    };
  }, [scheduleResumeCloudPull, currentUser?.uid, scheduleHistorySummaryWarmup]);

  useEffect(() => {
    let cancelled = false;
    if (!authReady || !currentUser?.uid || !accountSyncChecked || !checkComplete) return undefined;
    if (!isOnboarded) return undefined;
    if (needsNativeIap && !nativeSubActive) return undefined;
    if (getHasSeenWebAccessOffer()) return undefined;
    if (getWebAccessLinkOfferNeverAsk()) return undefined;

    void getLinkedProviderIdsLive().then((ids) => {
      if (cancelled) return;
      const hasGoogle = ids.includes('google.com');
      const hasApple = ids.includes('apple.com');
      if (hasGoogle && hasApple) {
        setHasSeenWebAccessOffer(true);
        return;
      }
      if (!hasGoogle && hasApple) {
        setWebAccessMissingProvider('google');
        setWebAccessOfferOpen(true);
        return;
      }
      if (hasGoogle && !hasApple) {
        if (shouldOfferLinkAppleNudge()) {
          setWebAccessMissingProvider('apple');
          setWebAccessOfferOpen(true);
        } else {
          setHasSeenWebAccessOffer(true);
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    authReady,
    currentUser?.uid,
    accountSyncChecked,
    checkComplete,
    isOnboarded,
    needsNativeIap,
    nativeSubActive,
  ]);

  useEffect(() => {
    const run = async () => {
      if (!needsNativeIap || !currentUser || !accountSyncChecked || !checkComplete) return;
      setNativeSubChecked(false);
      try {
        const active = await checkActiveProSubscription(currentUser.uid);
        setNativeSubActive(active);
      } catch (e) {
        console.warn('Subscription check failed:', e?.message || e);
        setNativeSubActive(false);
      } finally {
        setNativeSubChecked(true);
      }
    };
    void run();
  }, [needsNativeIap, currentUser, accountSyncChecked, checkComplete]);

  useEffect(() => {
    if (!authReady || !currentUser?.uid) return;
    ensureAiTrialStartedForUid(currentUser.uid);
  }, [authReady, currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid || !nativeSubChecked) return;
    ensureAiTrialStartedForUid(currentUser.uid);
    // Web: checkActiveProSubscription() short-circuits to true — never persist that as StoreKit Pro.
    if (requiresNativeSubscriptionGate()) {
      setCachedNativeSubActiveForUid(currentUser.uid, nativeSubActive);
    } else {
      setCachedNativeSubActiveForUid(currentUser.uid, false);
    }
    runFreemiumOpenSubscriptionNudge(currentUser.uid);
  }, [currentUser?.uid, nativeSubChecked, nativeSubActive]);

  useEffect(() => {
    const syncAccountFromCloud = async () => {
      if (!authReady) return;
      if (!currentUser) {
        setAccountSyncChecked(true);
        return;
      }
      try {
        // Device reset / account wipe saves migrated:false. Until onboarding finishes again, do not let
        // stale cloud prefs or journal-based heuristics resurrect migrated:true or cloud name/goals.
        const localBeforeCloud = getSettings();
        const honorLocalFreshOnboarding = !!(localBeforeCloud && localBeforeCloud.migrated === false);

        const cloudPrefs = await getCloudUserPreferences().catch(() => ({}));
        if (cloudPrefs && typeof cloudPrefs === 'object') {
          if (cloudPrefs.settings && typeof cloudPrefs.settings === 'object') {
            const local = getSettings() || {};
            const cs = cloudPrefs.settings;
            const mergedSettings = {
              ...local,
              name: honorLocalFreshOnboarding
                ? String(local.name || '').trim()
                : String(cs.name || local.name || '').trim(),
              goals: honorLocalFreshOnboarding
                ? Array.isArray(local.goals)
                  ? local.goals
                  : []
                : Array.isArray(cs.goals)
                  ? cs.goals
                  : Array.isArray(local.goals)
                    ? local.goals
                    : [],
              userPreferences:
                cs.userPreferences && typeof cs.userPreferences === 'object'
                  ? { ...(local.userPreferences || {}), ...cs.userPreferences }
                  : (local.userPreferences || {}),
              migrated: honorLocalFreshOnboarding
                ? false
                : Boolean(cs.migrated || local.migrated),
            };
            delete mergedSettings.prompts;
            saveSettings(mergedSettings);
          }
          if (typeof cloudPrefs.colorTheme === 'string') saveColorTheme(cloudPrefs.colorTheme);
          if (cloudPrefs.customThemePalette && typeof cloudPrefs.customThemePalette === 'object') {
            saveCustomThemePalette(cloudPrefs.customThemePalette);
          }
          if (typeof cloudPrefs.textScale === 'string') saveTextScale(cloudPrefs.textScale);
          if (cloudPrefs.voicePrefs && typeof cloudPrefs.voicePrefs === 'object') saveVoicePrefs(cloudPrefs.voicePrefs);
          if (cloudPrefs.homeChatState && typeof cloudPrefs.homeChatState === 'object') {
            setHomeChatState(pickPreferredHomeChatState(cloudPrefs.homeChatState, getHomeChatState()));
          }
          if (typeof cloudPrefs.hasSeenWebAccessOffer === 'boolean') {
            setHasSeenWebAccessOffer(cloudPrefs.hasSeenWebAccessOffer);
          }
          if (typeof cloudPrefs.webAccessLinkNeverAsk === 'boolean') {
            setWebAccessLinkOfferNeverAsk(cloudPrefs.webAccessLinkNeverAsk);
          }
        }

        const existing = getSettings() || {};
        const fallbackName = deriveSignInFirstName(currentUser);
        if (!String(existing?.name || '').trim() && fallbackName) {
          const seeded = { ...existing, name: fallbackName, migrated: Boolean(existing?.migrated) };
          delete seeded.prompts;
          saveSettings({
            ...seeded,
            goals: Array.isArray(existing?.goals) ? existing.goals : [],
          });
        }

        // Pull recent cloud entries into local device cache so History works across devices.
        const entries = await getCloudJournalEntries(100);
        const merged = mergeCloudEntriesIntoLocal(entries);
        if (merged > 0) {
          window.dispatchEvent(new Event('focus'));
        }
        scheduleHistorySummaryWarmup(800);

        // Skip onboarding only when cloud explicitly indicates onboarding was completed.
        const cloudMigrated = Boolean(cloudPrefs?.settings?.migrated);
        if (
          !honorLocalFreshOnboarding
          && !hasCompletedOnboarding()
          && cloudMigrated
          && Array.isArray(entries)
          && entries.length > 0
        ) {
          const existingAfterNameSeed = getSettings() || {};
          const fallbackNameWithDefault = deriveSignInFirstName(currentUser) || 'Friend';
          const skipOnboarding = {
            ...existingAfterNameSeed,
            name: String(existingAfterNameSeed?.name || '').trim() || fallbackNameWithDefault,
            goals: Array.isArray(existingAfterNameSeed?.goals) ? existingAfterNameSeed.goals : [],
            migrated: true,
          };
          delete skipOnboarding.prompts;
          saveSettings(skipOnboarding);
          setIsOnboarded(true);
        }
      } catch (error) {
        console.warn('Cloud account sync skipped:', error?.message || error);
      } finally {
        setAccountSyncChecked(true);
        setIsOnboarded(hasCompletedOnboarding());
      }
    };
    void syncAccountFromCloud();
  }, [authReady, currentUser, scheduleHistorySummaryWarmup]);

  useEffect(() => {
    scheduleHistorySummaryWarmup(1400);
    return () => {
      if (summaryWarmupTimerRef.current) {
        window.clearTimeout(summaryWarmupTimerRef.current);
        summaryWarmupTimerRef.current = null;
      }
    };
  }, [scheduleHistorySummaryWarmup]);

  const handleUnlock = () => {
    console.log('🔓 App: handleUnlock called, setting isLocked to false');
    setIsLocked(false);
  };

  useEffect(() => {
    const syncOnboarding = () => setIsOnboarded(hasCompletedOnboarding());
    window.addEventListener('rebuildme-onboarding-saved', syncOnboarding);
    return () => window.removeEventListener('rebuildme-onboarding-saved', syncOnboarding);
  }, []);

  // Show splash screen first
  if (showSplash) {
    return <SimpleSplash onComplete={handleSplashComplete} />;
  }

  if (!authReady) {
    return (
      <ThemeProvider>
        <div className="min-h-dvh bg-app flex flex-col items-center justify-center px-6 py-8">
          <AppSignInCard
            animated={false}
            canShowAppleAuth={canShowAppleAuth}
            acceptedTerms={acceptedTerms}
            onAcceptedTermsChange={handleAcceptedTermsChange}
            firebaseDiagnostics={firebaseDiagnostics}
            authError={authError}
            isSigningIn={isSigningIn}
            onGoogle={handleGoogleSignIn}
            onApple={handleAppleSignIn}
            demoMode={DEMO_MODE}
            demoEmail={demoEmail}
            demoPassword={demoPassword}
            onDemoEmailChange={setDemoEmail}
            onDemoPasswordChange={setDemoPassword}
            onDemoSignIn={handleDemoSignIn}
          />
        </div>
      </ThemeProvider>
    );
  }

  if (!currentUser) {
    return (
      <ThemeProvider>
        <div className="min-h-dvh bg-app flex flex-col items-center justify-center px-6 py-8">
          <AppSignInCard
            animated
            canShowAppleAuth={canShowAppleAuth}
            acceptedTerms={acceptedTerms}
            onAcceptedTermsChange={handleAcceptedTermsChange}
            firebaseDiagnostics={firebaseDiagnostics}
            authError={authError}
            isSigningIn={isSigningIn}
            onGoogle={handleGoogleSignIn}
            onApple={handleAppleSignIn}
            demoMode={DEMO_MODE}
            demoEmail={demoEmail}
            demoPassword={demoPassword}
            onDemoEmailChange={setDemoEmail}
            onDemoPasswordChange={setDemoPassword}
            onDemoSignIn={handleDemoSignIn}
          />
        </div>
      </ThemeProvider>
    );
  }

  if (currentUser && !accountSyncChecked) {
    return (
      <ThemeProvider>
        <div className="min-h-dvh bg-app flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md rounded-[2rem] border border-theme-secondary bg-theme-light p-6 shadow-ambient text-center">
            <p className="font-body text-sm text-secondary">Syncing your account...</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // Show loading message while checking security
  if (!checkComplete) {
    return (
      <div className="fixed inset-0 bg-app flex flex-col items-center justify-center gap-4">
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'var(--theme-accent)' }}
        />
        <p style={{ color: 'var(--theme-text-secondary, #73737D)', fontSize: '14px' }}>
          Loading app...
        </p>
      </div>
    );
  }

  if (currentUser && accountSyncChecked && needsNativeIap) {
    if (!nativeSubChecked) {
      return (
        <ThemeProvider>
          <div className="min-h-dvh bg-app flex flex-col items-center justify-center gap-4 px-6">
            <div
              className="w-8 h-8 rounded-full border-2 animate-spin"
              style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'var(--theme-accent)' }}
            />
            <p className="font-body text-sm text-secondary text-center">Checking subscription…</p>
          </div>
        </ThemeProvider>
      );
    }
    if (shouldShowSubscriptionPaywall) {
      return (
        <ThemeProvider>
          <SubscriptionGate
            firebaseUid={currentUser.uid}
            onUnlocked={() => setNativeSubActive(true)}
          />
        </ThemeProvider>
      );
    }
  }

  // Show passcode screen if locked
  const content = (isLocked && isOnboarded) ? (
    <>
      <Toaster 
        position="top-left" 
        offset={{ top: toastTopOffset, left: toastLeftOffset }}
        mobileOffset={{ top: toastTopOffset, left: toastLeftOffset }}
        toastOptions={{
          className: 'font-body text-left',
          style: {
            width: toastMaxWidth,
            maxWidth: toastMaxWidth,
            background: 'var(--theme-light, #F4F1FA)',
            border: '1px solid var(--theme-secondary, #E9E3F5)',
            color: 'var(--theme-text-primary, #2D2D35)',
          },
        }}
      />
      <Passcode onUnlock={handleUnlock} />
    </>
  ) : (
    <div className="App font-body">
      <Toaster 
        position="top-left" 
        offset={{ top: toastTopOffset, left: toastLeftOffset }}
        mobileOffset={{ top: toastTopOffset, left: toastLeftOffset }}
        toastOptions={{
          className: 'font-body text-left',
          style: {
            width: toastMaxWidth,
            maxWidth: toastMaxWidth,
            background: 'var(--theme-light, #F4F1FA)',
            border: '1px solid var(--theme-secondary, #E9E3F5)',
            color: 'var(--theme-text-primary, #2D2D35)',
          },
        }}
      />
      <BrowserRouter>
        <AnimatePresence mode="wait">
          <Routes>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/history" element={<History />} />
              <Route path="/historical-view/:date" element={<HistoricalView />} />
              <Route path="/export" element={<Export />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/paypal-return" element={<PaypalReturn />} />
              <Route path="/paypal-cancel" element={<PaypalCancel />} />
              <Route path="/help" element={<Help />} />
              <Route path="/ai-response" element={<AIResponse />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </AnimatePresence>
      </BrowserRouter>
    </div>
  );

  // Show main app
  return (
    <ThemeProvider>
      <WebAccessLinkOffer
        open={webAccessOfferOpen}
        onClose={() => setWebAccessOfferOpen(false)}
        missingProvider={webAccessMissingProvider}
      />
      {content}
    </ThemeProvider>
  );
}

export default App;